import React, { Component } from "react";
import {
  WindowScroller,
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import moment from "moment";
import json2csv from "json2csv";
import OrderPanel from "./../components/bulk/orderPanel.jsx";
import NoCardsToShow from "./../components/misc/noCardsToShow.jsx";
import TwilioVerifyModal from "./../components/popups/chooseVerification.jsx";
import TwilioEnterPhoneVerifyCode from "./../components/popups/enterPhoneVerifyCode.jsx";
import PhoneAttemptsExceeded from "./../components/popups/phoneAttemptsExceeded.jsx";
import Loading from "./../components/popups/loading.jsx";
import { capitalizeFirstLetter, currencyFormatter } from "./../utils/display";
import { connect } from "react-redux";
import { defaultAction } from "../actions/defaultAction";
import * as ActionTypes from "../constants/actionTypes";
import {
  cancelSellOrder,
  completeVerify,
  getSellOrder,
  getSellOrders,
  getSellPayment,
  sendClaim,
  sendSupportMsg,
  startVerify,
  updatePhoneNumberForOrder,
} from "../thunks/customerInfo";
import {
  sortAlphaAsc,
  sortAlphaDesc,
  sortNumsAsc,
  sortNumsDesc,
} from "../utils/sort";
import CancelSellOrderModal from "../components/popups/cancelSellOrder";
import SupportModal from "../components/popups/support";
import SupportCardLevelModal from "../components/popups/supportCardLevel";
import { HeadLine } from "../modules/HeadLine";
import styled from "@emotion/styled";

const DownloadLink = styled.a`
  background: #ffffff;
  border-radius: 80px;
  font-family: Montserrat, serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  color: #c7a878;
  border: 1px solid rgba(199, 168, 120, 0.32);
  height: 40px;
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: #c7a878;
    text-decoration: none;
    transform: translateY(-0.5px);
    border-color: #b37b28;
  }
`;

class Orders extends Component {
  constructor() {
    super();
    this.state = { linkedSellOrder: null };
    this._cache = new CellMeasurerCache({
      defaultHeight: 40,
      fixedWidth: true,
    });
    this._resizeAllFlag = false;
  }

  componentDidMount = () => {
    this.props.getSellOrders();
    this.props.setFieldToSort("order placed");
  };

  componentWillReceiveProps = (nextProps) => {
    const { ordersSell, setOrderTotal, params, setSearchText } = nextProps;

    if (
      params &&
      params.id &&
      !ordersSell.get("_sellOrdersThunkRunning") &&
      !ordersSell.get("_sellOrderThunkRunning") &&
      !ordersSell.get("_sellCardsThunkRunning") &&
      this.state.linkedSellOrder === null
    ) {
      const linkedSellOrder = ordersSell
        .get("sellOrders")
        .find((order) => order.id === params.id);

      this.setState({
        linkedSellOrder: Object.keys(linkedSellOrder).length
          ? linkedSellOrder
          : null,
      });

      if (Object.keys(linkedSellOrder).length) {
        setSearchText(linkedSellOrder.orderId.toString());
        this.chooseActivePanel(params.id);
      }
    }

    if (
      this.props.ordersSell.get("sellOrders") !== ordersSell.get("sellOrders")
    ) {
      const id = ordersSell.get("sellOrders")[0].id;
      const ordersTotalPast90Days = ordersSell
        .get("sellOrders")
        .filter((order) => {
          const date = moment.utc(order.date.placed).valueOf();
          const limit = +moment().subtract(90, "days").format("x");
          return date > limit;
        }).length;

      this.chooseActivePanel(id);
      setOrderTotal(ordersTotalPast90Days);
    }
  };

  componentWillUnmount = () => {
    this.props.resetActivePanel();
    this.props.clearSearchText();
    this.props.setFieldToSort("");
    this.props.setSortOrder("desc");
    this.setState({ linkedSellOrder: null });
  };

  _resizeAll = () => {
    this._cache.clearAll();
    if (this._list) {
      this._list.recomputeRowHeights();
    }
  };

  openCancelSellOrderModal = (e, id) => {
    e.stopPropagation();
    this.props.setSellOrderToCancel(id);
    this.props.openCancelSellOrderModal();
  };

  chooseActivePanel = (id) => {
    const {
      userActions,
      resetActivePanel,
      setActivePanel,
      resetSellOrder,
      resetSellOrderCards,
      getSellOrder,
      location,
    } = this.props;
    const currentPage = location.pathname;
    const panelAlreadyActive =
      userActions.get("activePanel") === id ? true : false;

    if (panelAlreadyActive) {
      return resetActivePanel();
    }

    resetSellOrder();
    resetSellOrderCards();
    getSellOrder(id);
    setActivePanel(id);
  };

  filterBySearch = (v) => {
    const { userActions, location } = this.props;
    const currentPage = location.pathname;
    const filterRowsById = (v) =>
      `#${v}`
        .toLowerCase()
        .indexOf(userActions.get("searchText").toLowerCase()) > -1;
    const filterOrderRows = (id, status) =>
      `#${id} ${status}`
        .toLowerCase()
        .indexOf(userActions.get("searchText").toLowerCase()) > -1;
    const filterCardRows = (id, merchant, number, type, refID, value) =>
      `#${id} ${merchant} xxxx${number} ${type} ${refID} $${value} N/A`
        .toLowerCase()
        .indexOf(userActions.get("searchText").toLowerCase()) > -1;

    if (currentPage.includes("orders")) {
      return filterOrderRows(v.orderId, v.status);
    } else if (currentPage.includes("payments")) {
      return filterRowsById(v.id);
    } else if (currentPage.includes("cards")) {
      return filterCardRows(
        v.internalId,
        v.merchantName,
        v.number,
        v.type,
        v.refID,
        v.enteredValue
      );
    } else if (currentPage.includes("deductions")) {
      return filterRowsById(v.id);
    } else {
      return filterOrderRows(v.orderId, v.status);
    }
  };

  sortRows = (a, b) => {
    const { userActions } = this.props;
    const sortOrder = userActions.get("sortOrder");

    switch (userActions.get("fieldToSort")) {
      case "order id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.orderId, b.orderId)
          : sortNumsDesc(a.orderId, b.orderId);
      case "order placed":
        if (a.date && b.date) {
          return sortOrder === "asc"
            ? sortNumsAsc(
                moment.utc(a.date.placed).valueOf(),
                moment.utc(b.date.placed).valueOf()
              )
            : sortNumsDesc(
                moment.utc(a.date.placed).valueOf(),
                moment.utc(b.date.placed).valueOf()
              );
        }
        break;
      case "order status":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.status, b.status)
          : sortAlphaDesc(a.status, b.status);
      case "order status":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.status, b.status)
          : sortAlphaDesc(a.status, b.status);
      case "offer amount":
        return sortOrder === "asc"
          ? sortNumsAsc(a.cardsValue, b.cardsValue)
          : sortNumsDesc(a.cardsValue, b.cardsValue);
      case "payment id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.id, b.id)
          : sortNumsDesc(a.id, b.id);
      case "payout amount":
        return sortOrder === "asc"
          ? sortNumsAsc(a.amount, b.amount)
          : sortNumsDesc(a.amount, b.amount);
      case "payment status":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.status, b.status)
          : sortAlphaDesc(a.status, b.status);
      case "card id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.internalId, b.internalId)
          : sortNumsDesc(a.internalId, b.internalId);
      case "merchant":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.merchantName, b.merchantName)
          : sortAlphaDesc(a.merchantName, b.merchantName);
      case "number":
        return sortOrder === "asc"
          ? sortNumsAsc(a.number, b.number)
          : sortNumsDesc(a.number, b.number);
      case "type":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.type, b.type)
          : sortAlphaDesc(a.type, b.type);
      case "ref id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.refID, b.refID)
          : sortNumsDesc(a.refID, b.refID);
      case "entered value":
        return sortOrder === "asc"
          ? sortNumsAsc(a.enteredValue, b.enteredValue)
          : sortNumsDesc(a.enteredValue, b.enteredValue);
      case "deduction id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.id, b.id)
          : sortNumsDesc(a.id, b.id);
      case "amount":
        return sortOrder === "asc"
          ? sortNumsAsc(a.amount, b.amount)
          : sortNumsDesc(a.amount, b.amount);
      case "date":
        return sortOrder === "asc"
          ? sortNumsAsc(
              moment.utc(a.date).valueOf(),
              moment.utc(b.date).valueOf()
            )
          : sortNumsDesc(
              moment.utc(a.date).valueOf(),
              moment.utc(b.date).valueOf()
            );

      default:
    }
  };

  openPaymentDetailsModal = (id) => {
    const { openPaymentDetailsModal, getSellPayment } = this.props;
    openPaymentDetailsModal();
    getSellPayment(id);
  };

  closeSupportCardLevelModal = (e) => {
    e.stopPropagation();
    this.props.closeSupportCardLevelModal();
  };

  isSortableField = (fieldToSort) => {
    const { userActions } = this.props;
    const caretClasses =
      userActions.get("sortOrder") === "asc"
        ? "fa fa-lg fa-caret-up mar-l-10"
        : "fa fa-lg fa-caret-down mar-l-10";
    return (
      fieldToSort === userActions.get("fieldToSort") && (
        <i className={caretClasses}></i>
      )
    );
  };

  closeSupportModal = (e) => {
    e.stopPropagation();
    this.props.closeSupportModal();
  };

  toggleHelpOptions = () => {
    const {
      userActions,
      openHelpOptions,
      closeHelpOptions,
      closeCardHelpButtons,
    } = this.props;

    if (userActions.get("helpOptionsOpen")) {
      closeHelpOptions();
      closeCardHelpButtons();
    } else {
      openHelpOptions();
    }
  };

  setSortOrderAndSortField = (fieldToSort, sortOrder) => {
    const { setSortOrder, setFieldToSort, resetActivePanel } = this.props;
    setFieldToSort(fieldToSort);
    sortOrder === "asc" ? setSortOrder("desc") : setSortOrder("asc");
    resetActivePanel();
  };

  updateSearchText = (e) => {
    const { resetActivePanel, setSearchText } = this.props;
    resetActivePanel();
    setSearchText(e.target.value.trim());
  };

  openSupportCardLevelModal = (e, payload) => {
    e.stopPropagation();
    this.props.setOrderSupportInfo(payload);
    this.props.openSupportCardLevelModal();
  };

  toggleCardHelpButtons = () => {
    const { userActions, showCardHelpButtons, closeCardHelpButtons } =
      this.props;

    if (userActions.get("showCardHelpButtons")) {
      closeCardHelpButtons();
    } else {
      showCardHelpButtons();
    }
  };

  openSupportModal = (e, payload) => {
    e.stopPropagation();
    this.props.setOrderSupportInfo(payload);
    this.props.openSupportModal();
  };

  render() {
    const {
      ordersSell,
      userActions,
      customerInfo,
      popups,
      closeCardHelpButtons,
      userInfo,
      closeAttemptsExceededBox,
      orderAssistance,
      form,
    } = this.props;

    const orders = ordersSell.get("sellOrders");
    const currentPage = location.pathname;
    const currentSellOrder = ordersSell.get("sellOrder");
    const currentSellCards =
      currentSellOrder && ordersSell.get("sellOrderCards").length
        ? ordersSell.get("sellOrderCards")
        : [];
    const isAwaitingVerification = orders.find(
      (order) => order.status === "Awaiting Verification"
    );
    const orderSupportInfoCommon = {
      name: `${customerInfo.get("firstname")} ${customerInfo.get("lastname")}`,
      email: customerInfo.get("email"),
      orderType: capitalizeFirstLetter("sell"),
    };

    const sellOrderRows = orders
      .filter(this.filterBySearch)
      .sort(this.sortRows);

    let csv;

    if (orders.length) {
      csv = json2csv({
        data: orders,
        fields: [
          {
            label: "order_id",
            value: ({ orderId }) => orderId.toString(),
          },
          { value: "status" },
          {
            label: "entered_amount",
            value: ({ cardsValue }) => currencyFormatter(cardsValue),
          },
          {
            label: "date_placed",
            value: ({ date }) => moment(date.placed).format("MMM D, YYYY"),
          },
        ],
      });
    }

    if (userInfo.get("displayLoading")) {
      return <Loading showLoading />;
    }

    return (
      <div
        style={{
          flex: "80%",
          padding: "2rem",
        }}
      >
        <HeadLine
          headingText={"Orders"}
          tagLine={"Quick summary of your recent sell orders"}
        />
        <div
          className="search-container"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            position: "relative",
            top: "-70px",
          }}
        >
          <div style={{ marginRight: "2rem" }}>
            <input
              className="activity-search-box pad-r-40"
              style={{
                background: "rgba(196, 196, 196, 0.14)",
                borderRadius: "80px",
                height: "40px",
                padding: "1rem",
              }}
              placeholder="Search Orders"
              onChange={this.updateSearchText}
            />
            <i className="fa fa-lg fa-search activity-search-icon"></i>
          </div>
          <DownloadLink
            href={`data:text/csv;charset=utf-8,${encodeURI(csv)}`}
            target="_blank"
            download="orders.csv"
            rel="noreferrer"
          >
            Download CSV
          </DownloadLink>
        </div>
        <div style={{ marginTop: "-70px" }}>
          {Object.keys(orders).length && isAwaitingVerification && (
            <div className="twilio-alert mar-b-20">
              <span
                style={{
                  margin: "1rem",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  alignContent: "center",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM9 12H7V7H9V12ZM8 6C7.4 6 7 5.6 7 5C7 4.4 7.4 4 8 4C8.6 4 9 4.4 9 5C9 5.6 8.6 6 8 6Z"
                    fill="#C4C4C4"
                  />
                </svg>
              </span>
              <strong style={{ paddingRight: ".5rem" }}>Wait!</strong>
              You haven't verified your order yet.
              <span
                className="acct-pg-edit-link"
                onClick={this.props.showTwilioPopup}
              >
                Click here
              </span>
              to verify and complete your order.
            </div>
          )}
          <table className="fixed-table panel-table-heading hide-sm">
            <thead>
              <tr>
                <th
                  className="panel-column pointer"
                  onClick={this.setSortOrderAndSortField.bind(
                    null,
                    "order id",
                    userActions.get("sortOrder")
                  )}
                >
                  <div className="responsive-panel">
                    <span>
                      <strong>Order ID</strong>
                    </span>
                    {this.isSortableField(
                      "order id",
                      userActions.get("sortOrder")
                    )}
                  </div>
                </th>
                <th
                  className="panel-column pointer"
                  onClick={this.setSortOrderAndSortField.bind(
                    null,
                    "order placed",
                    userActions.get("sortOrder")
                  )}
                >
                  <div className="responsive-panel">
                    <span>
                      <strong>Order Placed</strong>
                    </span>
                    {this.isSortableField(
                      "order placed",
                      userActions.get("sortOrder")
                    )}
                  </div>
                </th>
                <th
                  className="panel-column pointer"
                  onClick={this.setSortOrderAndSortField.bind(
                    null,
                    "order status",
                    userActions.get("sortOrder")
                  )}
                >
                  <div className="responsive-panel">
                    <span>
                      <strong>Status</strong>
                    </span>
                    {this.isSortableField(
                      "order status",
                      userActions.get("sortOrder")
                    )}
                  </div>
                </th>
                <th
                  className="panel-column pointer"
                  onClick={this.setSortOrderAndSortField.bind(
                    null,
                    "offer amount",
                    userActions.get("sortOrder")
                  )}
                >
                  <div className="responsive-panel">
                    <span>
                      <strong>Entered Amount</strong>
                    </span>
                    {this.isSortableField("offer amount")}
                  </div>
                </th>
                <th className="panel-column-sm" />
                <th className="panel-column-sm" />
              </tr>
            </thead>
          </table>

          <div className="order-panel-premier">
            {sellOrderRows.length ? (
              <WindowScroller>
                {({ height }) => (
                  <AutoSizer disableHeight>
                    {({ width }) => {
                      this._resizeAll();
                      return (
                        <List
                          height={height - 320}
                          overscanRowCount={4}
                          rowCount={sellOrderRows.length}
                          rowHeight={this._cache.rowHeight}
                          rowRenderer={({ key, index, parent, style }) => {
                            const order = sellOrderRows[index];
                            const activePanelClass =
                              userActions.get("activePanel") === order.id
                                ? `dynamic-panel-active`
                                : `dynamic-panel`;
                            return (
                              <CellMeasurer
                                cache={this._cache}
                                columnIndex={0}
                                key={key}
                                parent={parent}
                                rowIndex={index}
                              >
                                {({ measure }) => (
                                  <div
                                    className={activePanelClass}
                                    style={style}
                                    key={key}
                                  >
                                    <OrderPanel
                                      chooseActivePanel={this.chooseActivePanel}
                                      activePanel={userActions.get(
                                        "activePanel"
                                      )}
                                      orderType="Sell Order"
                                      id={order.id}
                                      orderId={order.orderId}
                                      status={order.status}
                                      date={order.date.placed}
                                      value={order.cardsValue}
                                      currentPage={currentPage}
                                      order={currentSellOrder}
                                      cards={currentSellCards}
                                      openSupportModal={this.openSupportModal}
                                      openSupportCardLevelModal={
                                        this.openSupportCardLevelModal
                                      }
                                      orderSupportInfoCommon={
                                        orderSupportInfoCommon
                                      }
                                      openCancelSellOrderModal={
                                        this.openCancelSellOrderModal
                                      }
                                      toggleHelpOptions={this.toggleHelpOptions}
                                      canCancel={order.canCancel}
                                      showLocalSpinner={popups.get(
                                        "showLocalSpinner"
                                      )}
                                      userActions={userActions}
                                      toggleCardHelpButtons={
                                        this.toggleCardHelpButtons
                                      }
                                      renderCardHelpButtons={userActions.get(
                                        "showCardHelpButtons"
                                      )}
                                      closeCardHelpButtons={
                                        closeCardHelpButtons
                                      }
                                      openPaymentDetailsModal={
                                        this.openPaymentDetailsModal
                                      }
                                    />
                                  </div>
                                )}
                              </CellMeasurer>
                            );
                          }}
                          width={width}
                        />
                      );
                    }}
                  </AutoSizer>
                )}
              </WindowScroller>
            ) : (
              <table className="fixed-table no-results">
                <tbody>
                  <tr>
                    <td className="panel-column" colSpan="6">
                      No Orders Found
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          <TwilioVerifyModal
            orderID={isAwaitingVerification ? isAwaitingVerification.id : null}
            updateThePhone={this.props.updateThePhone}
            setChangePhoneTxt={this.props.setChangePhoneTxt}
            changePhone={popups.get("changePhone")}
            showChoosePhoneOrTextBox={popups.get("showChoosePhoneOrTextBox")}
            phoneNumber={customerInfo.get("phone")}
            changePhoneTxt={customerInfo.get("changePhoneTxt")}
            startVerify={this.props.startVerify}
            showChangePhone={this.props.showChangePhone}
            hideChangePhone={this.props.hideChangePhone}
            hideChangePhoneError={this.props.hideChangePhoneError}
            _phoneErrorModal={customerInfo.get("_phoneErrorModal")}
          />
          <TwilioEnterPhoneVerifyCode
            completeVerify={this.props.completeVerify}
            passCode={customerInfo.get("passCode")}
            orderID={isAwaitingVerification ? isAwaitingVerification.id : null}
            verifyID={customerInfo.get("verifyID")}
            updatePassCode={this.props.updatePassCode}
            showEnterCodeBox={popups.get("showEnterCodeBox")}
            attempts={customerInfo.get("attempts")}
            makeAnotherPasscodeAttempt={this.props.makeAnotherPasscodeAttempt}
            location={this.props.location}
            customTwilioMsg={customerInfo.get("customTwilioMsg")}
          />
          <PhoneAttemptsExceeded
            popups={popups}
            closeAttemptsExceededBox={closeAttemptsExceededBox}
          />
        </div>
        <SupportModal
          open={popups.get("showSupportModal")}
          closeSupportModal={this.closeSupportModal}
          orderAssistance={orderAssistance}
          formData={form}
          customerInfo={customerInfo}
          orderId={orderAssistance.get("orderId")}
          sendSupportMessage={this.props.sendSupportMessage}
        />
        <SupportCardLevelModal
          open={popups.get("showSupportCardLevelModal")}
          closeSupportCardLevelModal={this.closeSupportCardLevelModal}
          orderAssistance={orderAssistance}
          formData={form}
          customerInfo={customerInfo}
          orderId={orderAssistance.get("orderId")}
          setClaimType={this.props.setClaimType}
          setClaimAmountToRefund={this.props.setClaimAmountToRefund}
          sendSupportMessage={this.props.sendSupportMessage}
          sendClaim={() => this.props.sendClaim}
        />
        <CancelSellOrderModal
          cancelSellOrder={this.props.cancelSellOrder}
          closeCancelSellOrderModal={this.props.closeCancelSellOrderModal}
          sellOrderToCancel={ordersSell.get("sellOrderToCancel")}
          open={popups.get("showCancelSellOrderModal")}
        />
      </div>
    );
  }
}

export default connect(
  (state) => ({
    ordersSell: state.ordersSell,
    userActions: state.userActions,
    customerInfo: state.customerInfo,
    popups: state.popups,
    userInfo: state.userInfo,
    orderAssistance: state.orderAssistance,
    form: state.form,
  }),
  (dispatch) => ({
    getSellOrders: () => dispatch(getSellOrders()),
    resetActivePanel: () =>
      dispatch(defaultAction(ActionTypes.RESET_ACTIVE_PANEL)),
    clearSearchText: () =>
      dispatch(defaultAction(ActionTypes.RESET_SEARCH_TEXT)),
    setFieldToSort: (v) =>
      dispatch(defaultAction(ActionTypes.SET_FIELD_TO_SORT, v)),
    setSortOrder: (v) => dispatch(defaultAction(ActionTypes.SET_SORT_ORDER, v)),
    openSupportCardLevelModal: () =>
      dispatch(defaultAction(ActionTypes.OPEN_SUPPORT_CARD_LEVEL_MODAL)),
    closeSupportCardLevelModal: () =>
      dispatch(defaultAction(ActionTypes.CLOSE_SUPPORT_CARD_LEVEL_MODAL)),
    openCancelSellOrderModal: () =>
      dispatch(defaultAction(ActionTypes.OPEN_CANCEL_SELL_ORDER_MODAL)),
    startVerify: (orderID, type) => dispatch(startVerify(orderID, type)),
    updateThePhone: (id, phone) =>
      dispatch(updatePhoneNumberForOrder(id, phone)),
    setChangePhoneTxt: (phoneNum) =>
      dispatch(defaultAction(ActionTypes.SET_CHANGE_PHONE_TXT, phoneNum)),
    hideChangePhone: () =>
      dispatch(defaultAction(ActionTypes.HIDE_CHANGE_PHONE)),
    hideChangePhoneError: () =>
      dispatch(defaultAction(ActionTypes.HIDE_CHANGE_PHONE_ERROR)),
    showTwilioPopup: () =>
      dispatch(defaultAction(ActionTypes.SHOW_TWILIO_POPUP)),
    makeAnotherPasscodeAttempt: () =>
      dispatch(defaultAction(ActionTypes.MAKE_ANOTHER_PASSCODE_ATTEMPT)),
    updatePassCode: (e) =>
      dispatch(
        defaultAction(
          ActionTypes.UPDATE_PASS_CODE,
          e.target.value.replace(/^[\s\uFEFF\xA0]+/g, "")
        )
      ),
    completeVerify: (orderID, verifyID, code, context) =>
      dispatch(completeVerify(orderID, verifyID, code, context)),
    closeCardHelpButtons: () =>
      dispatch(defaultAction(ActionTypes.CLOSE_CARD_HELP_BUTTONS)),
    closeAttemptsExceededBox: () =>
      dispatch(defaultAction(ActionTypes.CLOSE_ATTEMPTS_EXCEEDED_BOX)),
    openPaymentDetailsModal: () =>
      dispatch(defaultAction(ActionTypes.OPEN_PAYMENT_DETAILS_MODAL)),
    closePaymentDetailsModal: () =>
      dispatch(defaultAction(ActionTypes.CLOSE_PAYMENT_DETAILS_MODAL)),
    setOrderTotal: (orderTotal) =>
      dispatch(defaultAction(ActionTypes.SET_ORDER_TOTAL, { orderTotal })),
    resetSellOrder: () => dispatch(defaultAction(ActionTypes.RESET_SELL_ORDER)),
    resetSellOrderCards: () =>
      dispatch(defaultAction(ActionTypes.RESET_SELL_ORDER_CARDS)),
    getSellOrder: (id) => dispatch(getSellOrder(id)),
    setActivePanel: (id) =>
      dispatch(defaultAction(ActionTypes.SET_ACTIVE_PANEL, id)),
    setSearchText: (v) =>
      dispatch(defaultAction(ActionTypes.SET_SEARCH_TEXT, v)),
    cancelSellOrder: (id) => dispatch(cancelSellOrder(id)),
    closeCancelSellOrderModal: () =>
      dispatch(defaultAction(ActionTypes.CLOSE_CANCEL_SELL_ORDER_MODAL)),
    sendSupportMessage: (payload) => dispatch(sendSupportMsg(payload)),
    setClaimAmountToRefund: (refundAmount) =>
      dispatch(
        defaultAction(ActionTypes.SET_CLAIM_AMOUNT_TO_REFUND, refundAmount)
      ),
    setClaimType: (id) =>
      dispatch(defaultAction(ActionTypes.SET_CLAIM_TYPE, id)),
    getSellPayment: (id) => dispatch(getSellPayment(id)),
    setOrderSupportInfo: (payload) =>
      dispatch(defaultAction(ActionTypes.SET_ORDER_SUPPORT_INFO, payload)),
    openSupportModal: () =>
      dispatch(defaultAction(ActionTypes.OPEN_SUPPORT_MODAL)),
    closeSupportModal: () =>
      dispatch(defaultAction(ActionTypes.CLOSE_SUPPORT_MODAL)),
    sendClaim: (payload) => dispatch(sendClaim(payload)),
    setSellOrderToCancel: (id) =>
      dispatch(defaultAction(ActionTypes.SET_SELL_ORDER_TO_CANCEL, id)),
  })
)(Orders);
